import { getBlockId } from '../cms.link'

/**
 * The block types LONGY-952 removed from the catalogue.
 *
 * A frozen literal list, deliberately not `Object.values(BlockType)`. Deriving the rule from the
 * live catalogue would make this a general "delete anything the code no longer knows about"
 * script: run from a different checkout, or after the next catalogue change, and the same file
 * would quietly destroy a different and larger set of blocks. What it may delete is fixed here, in
 * the commit that removed them.
 */
export const REMOVED_BLOCK_TYPES = [
    'HOMEPAGE_HERO',
    'PAGE_HERO',
    'TEXT_CTA',
    'ABSTRACT',
    'CAPTION',
    'LIST',
    'IMAGE_LIST',
    'ICON_LIST',
    'ICON_GRID',
    'IMAGE_CAROUSEL',
    'CAROUSEL',
    'CARD_GRID',
    'IMAGE_CTA',
    'IMAGE_TEXT_CTA',
    'ASYMMETRIC_CTA',
    'BANNER',
    'FLAT_BANNER',
    'TABLE',
    'TIMELINE',
    'PROGRESS_STEPS',
    'SPACER',
    'TABS',
    'ARTICLE_CARDS',
    'ARTICLE_LIST',
    'ARTICLE_HEADER',
] as const

export type RemovedBlockType = (typeof REMOVED_BLOCK_TYPES)[number]

const REMOVED = new Set<string>(REMOVED_BLOCK_TYPES)

/** What was dropped, so an operator reads block ids rather than a count. */
export type PrunedBlock = { id: string | null; type: string }

export type PrunedBlocks = {
    kept: unknown[]
    removed: PrunedBlock[]
    /**
     * `block:<id>` values in the surviving blocks whose target was just pruned.
     *
     * Reported, never repaired: the anchor's replacement is an editorial decision, and a renderer
     * already degrades an unresolvable target to unlinked text rather than failing. Without the
     * report the breakage is invisible until a visitor clicks it.
     */
    orphanedAnchors: string[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value)

const collectAnchorIds = (value: unknown, out: Set<string>): void => {
    if (typeof value === 'string') {
        const blockId = getBlockId(value)
        if (blockId !== undefined && blockId !== '') out.add(blockId)
        return
    }
    if (Array.isArray(value)) {
        for (const item of value) collectAnchorIds(item, out)
        return
    }
    if (isRecord(value)) {
        for (const item of Object.values(value)) collectAnchorIds(item, out)
    }
}

/**
 * Drop the blocks whose type the catalogue no longer has, and renumber what is left.
 *
 * An entry that is not an object, or carries no recognisable type, is removed too and reported as
 * such. Silently dropping it instead would let the log disagree with the write — the stored json
 * is not schema-checked, so "not a block" is a real state a row can be in.
 */
export const pruneRemovedBlocks = (blocks: unknown): PrunedBlocks => {
    if (!Array.isArray(blocks)) return { kept: [], removed: [], orphanedAnchors: [] }

    const kept: unknown[] = []
    const removed: PrunedBlock[] = []

    for (const block of blocks) {
        if (!isRecord(block)) {
            removed.push({ id: null, type: '(not a block)' })
            continue
        }

        const type = typeof block['type'] === 'string' ? block['type'] : ''
        const id = typeof block['id'] === 'string' ? block['id'] : null

        if (type === '' || REMOVED.has(type)) {
            removed.push({ id, type: type === '' ? '(untyped)' : type })
            continue
        }

        kept.push(block)
    }

    const renumbered = kept.map((block, order) => ({
        ...(block as Record<string, unknown>),
        order,
    }))

    const removedIds = new Set(
        removed.map((block) => block.id).filter((id): id is string => id !== null),
    )
    const anchors = new Set<string>()
    collectAnchorIds(renumbered, anchors)

    return {
        kept: renumbered,
        removed,
        orphanedAnchors: [...anchors].filter((id) => removedIds.has(id)),
    }
}
